/**
 * 模板列表页面
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { templateApi } from '../services/templateApi';
import { TemplateItem } from '../types';

const TemplateList: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [industry, setIndustry] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, category, industry]);

  useEffect(() => {
    templateApi.getCategories().then(setCategories).catch(() => {});
    templateApi.getIndustries().then(setIndustries).catch(() => {});
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const result = await templateApi.getTemplates({
        page,
        page_size: pageSize,
        search: search || undefined,
        category: category || undefined,
        industry: industry || undefined,
      });
      setTemplates(result.templates || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('加载模板列表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadTemplates();
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="max-w-6xl mx-auto py-2">
      {/* 头部标题与操作 */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">模板管理</h1>
          <p className="text-xs text-muted-foreground mt-1">上传及管理您的 Word/PDF 标书格式模板</p>
        </div>
        <button
          onClick={() => navigate('/templates/new')}
          className="inline-flex items-center px-4 py-2.5 bg-brand-700 hover:bg-[#0042b3] text-white text-sm font-semibold rounded-xl transition-all duration-300 shadow-[0_4px_12px_rgba(0,82,217,0.15)] active:scale-[0.98] transform"
        >
          <svg className="w-4 h-4 mr-1.5 stroke-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          上传模板
        </button>
      </div>

      {/* 搜索和筛选 */}
      <div className="bg-white rounded-2xl border border-border p-4 mb-8 shadow-[0_4px_16px_rgba(0,0,0,0.015)]">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索模板名称..."
              className="w-full pl-10 pr-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-[#0052d9] focus:ring-1 focus:ring-[#0052d9]/40 transition-all placeholder-gray-400"
            />
            <svg className="w-5 h-5 text-muted-foreground absolute left-3.5 top-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div className="relative">
            <input
              type="text"
              value={category}
              onChange={(e) => { setCategory(e.target.value); setPage(1); }}
              list="cat-list"
              placeholder="全部分类筛选"
              className="w-40 px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-[#0052d9] focus:ring-1 focus:ring-[#0052d9]/40 bg-white transition-all placeholder-gray-400"
            />
            <datalist id="cat-list">
              {categories.map((c) => (<option key={c} value={c} />))}
            </datalist>
          </div>
          <div className="relative">
            <input
              type="text"
              value={industry}
              onChange={(e) => { setIndustry(e.target.value); setPage(1); }}
              list="ind-list"
              placeholder="全部行业筛选"
              className="w-40 px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-[#0052d9] focus:ring-1 focus:ring-[#0052d9]/40 bg-white transition-all placeholder-gray-400"
            />
            <datalist id="ind-list">
              {industries.map((ind) => (<option key={ind} value={ind} />))}
            </datalist>
          </div>
          
          <button
            type="submit"
            className="px-5 py-2.5 bg-accent hover:bg-accent text-foreground text-sm font-semibold border border-border rounded-xl transition-colors"
          >
            搜索
          </button>
          
          {(category || industry || search) && (
            <button
              type="button"
              onClick={() => { setSearch(''); setCategory(''); setIndustry(''); setPage(1); }}
              className="px-3.5 py-2 text-xs font-semibold text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl transition-colors"
            >
              重置筛选
            </button>
          )}
        </form>
      </div>

      {/* 模板卡片网格 */}
      {loading ? (
        <div className="flex justify-center items-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052d9]"></div>
          <span className="ml-3 text-sm text-muted-foreground font-medium">加载标书模板中...</span>
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-border shadow-[0_4px_16px_rgba(0,0,0,0.01)] px-4">
          <svg className="w-12 h-12 text-muted-foreground mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          <p className="text-sm text-muted-foreground font-medium mb-4">暂无可用模板数据</p>
          <button
            onClick={() => navigate('/templates/new')}
            className="inline-flex items-center px-4 py-2 border border-border text-xs font-semibold rounded-xl text-primary bg-white hover:bg-accent transition-all shadow-sm"
          >
            上传您的第一个格式模板
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((tpl) => (
              <div
                key={tpl.id}
                onClick={() => navigate(`/templates/${tpl.id}`)}
                className="bg-white rounded-2xl border border-border p-5 cursor-pointer shadow-[0_12px_33px_0_rgba(0,0,0,0.015),0_3.6px_9.9px_0_rgba(0,0,0,0.01)] hover:-translate-y-1.5 hover:shadow-[0_20px_40px_rgba(0,0,0,0.04),0_5px_15px_rgba(0,0,0,0.02)] hover:border-[#0052d9]/25 transition-all duration-300 flex flex-col justify-between min-h-[145px]"
              >
                <div>
                  <div className="flex items-start justify-between mb-3 gap-3">
                    <h3 className="text-sm font-bold text-foreground truncate flex-1 hover:text-primary transition-colors" title={tpl.template_name}>
                      {tpl.template_name}
                    </h3>
                    <div className="flex gap-1.5 flex-shrink-0">
                      {tpl.is_system && (
                        <span className="px-1.5 py-0.5 text-[9px] font-bold bg-accent border border-border/50 text-muted-foreground rounded-lg">系统</span>
                      )}
                      <span className={`px-1.5 py-0.5 text-[9px] font-bold border rounded-lg shadow-sm ${
                        tpl.is_public
                          ? 'bg-[#eafaf1] border-[#d1f2e1]/80 text-[#18a058]'
                          : 'bg-accent border-border/60 text-muted-foreground'
                      }`}>
                        {tpl.is_public ? '公开' : '私有'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {tpl.category && (
                      <span className="px-2 py-0.5 text-[10px] font-semibold bg-primary/10 text-primary border border-[#dbeafe]/80 rounded-lg">
                        {tpl.category}
                      </span>
                    )}
                    {tpl.industry && (
                      <span className="px-2 py-0.5 text-[10px] font-semibold bg-purple-50 text-purple-600 border border-purple-100 rounded-lg">
                        {tpl.industry}
                      </span>
                    )}
                  </div>
                </div>

                {(() => {
                  // 合并 body_style 和 heading_styles 显示样式预览
                  const si = tpl.style_info as any;
                  if (!si) return null;
                  const entries: [string, any][] = [];
                  if (si.body_style) entries.push(['正文', si.body_style]);
                  const hs = si.heading_styles as any;
                  if (hs) {
                    (['Heading1','Heading2','Heading3'] as const).forEach(k => {
                      if (hs[k]) entries.push([{ Heading1:'一级标题', Heading2:'二级标题', Heading3:'三级标题' }[k]!, hs[k]]);
                    });
                  }
                  if (!entries.length) return null;
                  return (
                    <div className="mb-2 text-[10px] text-muted-foreground">
                      {entries.slice(0, 3).map(([k, v]) => (
                        <span key={k} className="mr-2">
                          {k}: {v.font_name || '宋体'} {v.font_size || ''}{v.font_size ? 'pt' : ''}
                        </span>
                      ))}
                      {entries.length > 3 && <span>+{entries.length - 3}</span>}
                    </div>
                  );
                })()}
                <div className="flex justify-between items-center text-[10px] text-muted-foreground pt-2.5 border-t border-border/80 mt-2.5">
                  <span>
                    配置于 {new Date(tpl.created_at).toLocaleDateString()}
                  </span>
                  <span className="font-bold text-primary hover:underline flex items-center gap-0.5">
                    查看详情
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* 分页 */}
          {totalPages > 1 && (
            <div className="mt-10 flex justify-center">
              <nav className="flex items-center space-x-1.5">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="px-3.5 py-1.5 border border-border rounded-xl text-xs font-semibold text-foreground bg-white hover:bg-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  上一页
                </button>
                <span className="px-4 py-1.5 text-xs text-muted-foreground font-medium">
                  第 <span className="font-semibold text-foreground">{page}</span> / {totalPages} 页
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  className="px-3.5 py-1.5 border border-border rounded-xl text-xs font-semibold text-foreground bg-white hover:bg-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  下一页
                </button>
              </nav>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TemplateList;
